import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';

function BannerSucesso({ mensagem }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-lg">
      <svg className="w-4 h-4 text-green-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
      <p className="text-sm text-green-700">{mensagem}</p>
    </div>
  );
}

function BannerErro({ mensagem }) {
  if (!mensagem) return null;
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
      <svg className="w-4 h-4 text-red-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <p className="text-sm text-red-700">{mensagem}</p>
    </div>
  );
}

function campoEnderecoVazio() {
  return {
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: '',
    cep: '',
  };
}

export default function DadosGerais({ dados, onboardingId, atualizarDados }) {
  const readOnly = !!dados.finalizado;

  const [emailCobranca, setEmailCobranca] = useState(
    dados.dadosGerais?.emailCobranca || ''
  );
  const [endereco, setEndereco] = useState({
    ...campoEnderecoVazio(),
    ...(dados.dadosGerais?.endereco || {}),
  });

  const [salvando, setSalvando] = useState(false);
  const [sucesso, setSucesso] = useState('');
  const [erro, setErro] = useState('');
  const [buscandoCep, setBuscandoCep] = useState(false);
  const timerSucesso = useRef(null);

  // Sincroniza se dados mudarem externamente
  useEffect(() => {
    setEmailCobranca(dados.dadosGerais?.emailCobranca || '');
    setEndereco({
      ...campoEnderecoVazio(),
      ...(dados.dadosGerais?.endereco || {}),
    });
  }, [dados]);

  function atualizarEndereco(campo, valor) {
    setEndereco(prev => ({ ...prev, [campo]: valor }));
  }

  async function buscarCep(cep) {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;
    setBuscandoCep(true);
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const json = await resp.json();
      if (!json.erro) {
        setEndereco(prev => ({
          ...prev,
          logradouro: json.logradouro || prev.logradouro,
          bairro: json.bairro || prev.bairro,
          cidade: json.localidade || prev.cidade,
          uf: json.uf || prev.uf,
        }));
      }
    } catch {
      // falha silenciosa na busca do CEP
    } finally {
      setBuscandoCep(false);
    }
  }

  async function handleSalvar(e) {
    e.preventDefault();
    if (readOnly) return;
    setSalvando(true);
    setSucesso('');
    setErro('');
    try {
      await api.put(`/onboardings/${onboardingId}/dados-gerais`, {
        emailCobranca,
        endereco,
      });
      await atualizarDados();
      setSucesso('Dados gerais salvos com sucesso.');
      if (timerSucesso.current) clearTimeout(timerSucesso.current);
      timerSucesso.current = setTimeout(() => setSucesso(''), 4000);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form onSubmit={handleSalvar} className="space-y-6">
      <div>
        <h1 className="section-title">Dados Gerais</h1>
        <p className="section-sub">
          Preencha o e-mail de cobrança e o endereço principal da sua empresa.
        </p>
      </div>

      {sucesso && <BannerSucesso mensagem={sucesso} />}
      {erro && <BannerErro mensagem={erro} />}

      {/* E-mail de Cobrança */}
      <div className="card space-y-4">
        <h2 className="text-base font-semibold text-gray-800">E-mail de Cobrança</h2>
        <div>
          <label className="label">E-mail de cobrança *</label>
          <input
            type="email"
            value={emailCobranca}
            onChange={e => setEmailCobranca(e.target.value)}
            disabled={readOnly}
            className="input"
            placeholder="financeiro@suaempresa.com.br"
            required
          />
          <p className="text-xs text-gray-400 mt-1">
            E-mail para envio de cobranças e faturas da HeadThink.
          </p>
        </div>
      </div>

      {/* Endereço */}
      <div className="card space-y-4">
        <h2 className="text-base font-semibold text-gray-800">Endereço</h2>

        {/* CEP */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="label">CEP *</label>
            <div className="relative">
              <input
                type="text"
                value={endereco.cep}
                onChange={e => atualizarEndereco('cep', e.target.value.replace(/\D/g, '').slice(0, 8))}
                onBlur={e => buscarCep(e.target.value)}
                disabled={readOnly}
                className="input"
                placeholder="00000-000"
                maxLength={8}
                required
              />
              {buscandoCep && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
                </div>
              )}
            </div>
          </div>

          <div className="sm:col-span-2">
            <label className="label">Logradouro *</label>
            <input
              type="text"
              value={endereco.logradouro}
              onChange={e => atualizarEndereco('logradouro', e.target.value)}
              disabled={readOnly}
              className="input"
              placeholder="Rua, Avenida, Travessa..."
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="label">Número *</label>
            <input
              type="text"
              value={endereco.numero}
              onChange={e => atualizarEndereco('numero', e.target.value)}
              disabled={readOnly}
              className="input"
              placeholder="123"
              required
            />
          </div>
          <div className="sm:col-span-2">
            <label className="label">Complemento</label>
            <input
              type="text"
              value={endereco.complemento}
              onChange={e => atualizarEndereco('complemento', e.target.value)}
              disabled={readOnly}
              className="input"
              placeholder="Apto, Sala, Bloco (opcional)"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="label">Bairro *</label>
            <input
              type="text"
              value={endereco.bairro}
              onChange={e => atualizarEndereco('bairro', e.target.value)}
              disabled={readOnly}
              className="input"
              placeholder="Bairro"
              required
            />
          </div>
          <div>
            <label className="label">Cidade *</label>
            <input
              type="text"
              value={endereco.cidade}
              onChange={e => atualizarEndereco('cidade', e.target.value)}
              disabled={readOnly}
              className="input"
              placeholder="Cidade"
              required
            />
          </div>
          <div>
            <label className="label">UF *</label>
            <input
              type="text"
              value={endereco.uf}
              onChange={e => atualizarEndereco('uf', e.target.value.toUpperCase().slice(0, 2))}
              disabled={readOnly}
              className="input"
              placeholder="SP"
              maxLength={2}
              required
            />
          </div>
        </div>
      </div>

      {!readOnly && (
        <div className="flex justify-end pb-8">
          <button type="submit" disabled={salvando} className="btn-primary">
            {salvando ? 'Salvando...' : 'Salvar Dados Gerais'}
          </button>
        </div>
      )}
    </form>
  );
}
